// Reverse-maps an on-disk layer-file path to the SPA route whose editor edits it.
//
// This is the inverse of how the editor views build file paths from their route params
// (ConfigEditorView/GroupEditorView/ServiceEditorView). The path grammar is the stable
// on-disk layout the whole config server is built around, so a focused parser over the
// three known shapes is safe — and far less fragile than it would be over an arbitrary
// filesystem. Segments are encodeURIComponent'd to match exactly how those views
// construct their own URLs.
//
// On-disk file-path shapes (verified against FileTree.vue / ServiceEditorView.vue —
// note there is NO `projects/` literal in a project-layer path; the route HAS one):
//   groups/{group}/{slot}.json                 (group layer, 3 segments)
//     -> /groups/{group}                              (slot === _base)
//     -> /groups/{group}/environments/{slot}          (otherwise)
//   groups/{group}/{project}/{slot}.json        (project layer, 4 segments)
//     -> /groups/{group}/projects/{project}
//     -> /groups/{group}/projects/{project}/environments/{slot}
//   services/{name}/{slot}.json                 (service layer, 3 segments)
//     -> /services/{name}
//     -> /services/{name}/environments/{slot}
//
// Returns null for anything unrecognised (e.g. _meta.json — metadata is edited via a
// modal, never the slot editor, so it never appears as a recoverable draft anyway).
// Callers treat null as "stage it into the store but don't navigate".
export function routeForFilePath(path: string): string | null {
  const enc = encodeURIComponent;
  const parts = path.split('/');

  const slotOf = (file: string): string | null =>
    file.endsWith('.json') ? file.slice(0, -'.json'.length) : null;

  const envTail = (slot: string): string =>
    slot === '_base' ? '' : `/environments/${enc(slot)}`;

  if (parts[0] === 'groups') {
    // groups/{group}/{slot}.json — group layer.
    if (parts.length === 3) {
      const slot = slotOf(parts[2]);
      if (!slot) return null;
      return `/groups/${enc(parts[1])}${envTail(slot)}`;
    }
    // groups/{group}/{project}/{slot}.json — project layer. The file path has no
    // `projects/` segment but the editor route does.
    if (parts.length === 4) {
      const slot = slotOf(parts[3]);
      if (!slot) return null;
      return `/groups/${enc(parts[1])}/projects/${enc(parts[2])}${envTail(slot)}`;
    }
    return null;
  }

  // services/{name}/{slot}.json — service layer.
  if (parts[0] === 'services' && parts.length === 3) {
    const slot = slotOf(parts[2]);
    if (!slot || slot.startsWith('_') && slot !== '_base') return null;
    return `/services/${enc(parts[1])}${envTail(slot)}`;
  }

  return null;
}
